// frontend/src/utils/notificationsRealtime.ts
// NEW FILE (2026-06-29) — BOUNDARY A: live notification bell via AURA's OWN
// authenticated SSE stream. NO Supabase client lib, NO anon key, NO Supabase
// JWT in the browser.
//
// AURA's web auth is COOKIE-based (api.ts uses withCredentials; the backend
// `authenticate` middleware reads req.cookies.token). Native EventSource sends
// same-origin cookies automatically, and /api/v1/notifications/stream is
// same-origin — so the SSE connection authenticates with the existing session
// cookie. No token plumbing required.
//
// On each `notification` event we call onInsert() (the caller re-loads the bell
// feed). Returns an unsubscribe fn. EventSource auto-reconnects on drop; we add
// nothing on top. If realtime is dark/unavailable the stream simply stays quiet
// and the caller's fallback poll keeps the bell fresh.

export type Unsub = () => void;

/**
 * Subscribe to AURA's live notification SSE stream for the logged-in user.
 * @param onInsert called whenever a `notification` event arrives.
 * @returns an unsubscribe function.
 */
export function subscribeNotifications(onInsert: () => void): Unsub {
  // EventSource isn't available during SSR / very old browsers — degrade.
  if (typeof window === 'undefined' || typeof EventSource === 'undefined') {
    return () => {};
  }

  let es: EventSource | null = null;
  try {
    // Same-origin path; withCredentials so the auth cookie rides along.
    es = new EventSource('/api/v1/notifications/stream', { withCredentials: true });

    // The backend emits named `notification` events; listen specifically.
    es.addEventListener('notification', () => onInsert());

    // Swallow errors — EventSource retries automatically. (A 401 will close it;
    // the api.ts 401 interceptor handles the login redirect on the next call.)
    es.onerror = () => {
      /* auto-retry; no action needed */
    };
  } catch {
    return () => {};
  }

  return () => {
    try {
      es?.close();
    } catch {
      /* ignore */
    }
  };
}
